// @ts-nocheck
import React, { useEffect, useState } from "react";
// import { uploadData } from "@aws-amplify/storage";
import { uploadData, downloadData } from "@aws-amplify/storage";
import { get, put, del, post } from "aws-amplify/api"
import { PageContainer, PageHeader } from "../components/AssetBreadCrumb";
import { PageBackButton } from "../components/PageChrome";
import { modalViewsConst, setModalState } from "../store/modalSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchPCurves, fetchSolarEnergyModels } from "../store/pCurveSlice";
import { PCurveTable } from "../components/PerformanceCurve/PCurveTable";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { generateClient } from "aws-amplify/api";
import { createUploadData, updateUploadData } from "../graphql/mutations";
import Papa from "papaparse";

import Select from "../components/ReusableComponents/Select";

// The label row repeats a dozen times on this form, so it lives in one place
const FieldLabel = ({ label, error }: { label: string; error?: string }) => (
  <div className="mb-2 flex items-baseline justify-between gap-3">
    <span className="eai-meta-label">{label}</span>
    {error && (
      <span className="font-inter text-eai-body-xs text-eai-danger-text">{error}</span>
    )}
  </div>
);

const PREVIEW_HEAD_CLASS =
  "whitespace-nowrap border-b border-eai-border bg-eai-surface-hover px-3 py-2 font-outfit text-eai-meta font-medium uppercase text-eai-ink-subtle";

const PARAMETER_OPTIONS = [
  { value: "Wind_Speed", label: "Wind Speed" },
  { value: "Wind_Direction", label: "Wind Direction" },
  { value: "Temperature", label: "Temperature" },
  { value: "Precipitation", label: "Precipitation" },
  { value: "Humidity", label: "Humidity" },
  { value: "Solar_Irradiance_short", label: "Solar Irradiance Short Wave" },
  { value: "Solar_Irradiance_long", label: "Solar Irradiance Long Wave" },
];

// The four paired dropdowns, so the markup below is one loop instead of four copies
const SELECT_FIELDS = [
  {
    name: "objective",
    label: "Objective",
    placeholder: "Select Objective",
    options: [
      { value: "min_RMSE", label: "Minimize RMSE" },
      { value: "min_MAE", label: "Minimize MAE" },
      { value: "Match_Peaks", label: "Match Peaks" },
    ],
  },
  {
    name: "optimizationHorizon",
    label: "Optimization Horizon",
    placeholder: "Select Horizon",
    options: [
      { value: "1_day", label: "1 Day" },
      { value: "3_days", label: "3 Days" },
      { value: "7_days", label: "7 Days" },
      { value: "1_month", label: "1 Month" },
    ],
  },
  {
    name: "temporalResolution",
    label: "Temporal Resolution",
    placeholder: "Select Resolution",
    options: [
      { value: "1_hour", label: "1 Hour" },
      { value: "3_hours", label: "3 Hours" },
      { value: "6_hours", label: "6 Hours" },
      { value: "24_hours", label: "24 Hours" },
    ],
  },
  {
    name: "seasonality",
    label: "Seasonality",
    placeholder: "Select Seasonality",
    options: [
      { value: "weekly", label: "Weekly" },
      { value: "monthly", label: "Monthly" },
      { value: "quarterly", label: "Quarterly" },
      { value: "annual", label: "Annual" },
    ],
  },
];
const UploadRecordSchema = z.object({
  name: z.string().trim().min(2).max(50),
  // assetGroupId: z .string() .min(5, { message: "Asset must belong to Asset Group" }),
  description: z.string().trim().max(150),
  parameter: z.string().trim().max(150),
  objective: z.string().min(1, "Please select an objective"), // Dropdown 1
  optimizationHorizon: z
    .string()
    .min(1, "Please select an optimization horizon"), // Dropdown 2
  temporalResolution: z.string().min(1, "Please select a temporal resolution"), // Dropdown 3
  seasonality: z.string().min(1, "Please select seasonality option"), // Dropdown 4
});
type UploadRecordType = z.infer<typeof UploadRecordSchema>;

const CustomModelForm = ({ initialUploadForm, editFlag }) => {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<UploadRecordType>({
    defaultValues: {
      name: initialUploadForm?.name || "",
      description: initialUploadForm?.description || "",
      parameter: initialUploadForm?.parameter || "",
      objective: initialUploadForm?.objective || "", // Initial value for new dropdown
      optimizationHorizon: initialUploadForm?.optimizationHorizon || "", // Initial value for new dropdown
      temporalResolution: initialUploadForm?.temporalResolution || "", // Initial value for new dropdown
      seasonality: initialUploadForm?.seasonality || "", // Initial value for new dropdown
    },
    resolver: zodResolver(UploadRecordSchema),
    mode: "onChange",
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pCurve = useSelector((state) => state.pCurve);
  const [uploadNewFileFlag, setUploadeNewFileFlag] = useState(editFlag);
  const [csvData, setCsvData] = useState([]);
  const [file, setFile] = useState(null);
  const [objective, setObjective] = useState("");
  const [llmJustification, setLLMJustification] = useState("");
  const [loadingLLMflag, setLoadingLLMflag] = useState(false);

  const getS3csv = async () => {
    try {
      // Download the CSV file from S3 using `downloadData`
      const result = await downloadData({
        path: initialUploadForm.fileLocation,
      }).result;

      // Use FileReader to read the Blob content as text
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvText = event.target.result;
        parseCsv(csvText); // Parse the CSV content
      };

      // Read the file content as text (assuming result.Body is a Blob)
      reader.readAsText(result.body);
    } catch (error) {
      console.error("Error downloading and parsing CSV:", error);
    }
  };

  useEffect(() => {
    if (editFlag && initialUploadForm.fileLocation) {
      getS3csv();
    }
  }, []);

  const handleNewPCurve = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.energyModelSelect,
        properties: null,
      })
    );
  };

  const handleFileChange = (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) {
      return;
    }
    setFile(selectedFile);
    parseCsv(selectedFile);
  };

  // Function to parse CSV using PapaParse
  const parseCsv = (file) => {
    Papa.parse(file, {
      complete: (result) => {
        setCsvData(result.data); // Set the parsed data to state
      },
      header: true, // Optional: Treat the first row as headers
      skipEmptyLines: true, // Skip empty lines
    });
  };

  // Corrected onSubmit function
  const onSubmit = async (formData: UploadRecordType) => {
    const client = generateClient();

    if (file) {
      try {
        // Generate a unique file name and upload to S3
        const fileName = `uploads/${file.name}`;

        const result = await uploadData({
          path: fileName,
          data: file,
        }).result;

        // Retrieve the file location URL after upload
        const fileLocation = result.path; // S3 key of the uploaded file

        // Include the file location in the form data
        formData.fileLocation = fileLocation;
      } catch (e) {
        console.log("Erropr", e);
      }
    }

    if (editFlag) {
      try {
        const { data } = await client.graphql({
          query: updateUploadData,
          variables: {
            input: {
              id: initialUploadForm.id,
              ...formData,
            },
          },
        });
      } catch (error) {
        console.log("error", error);
      }
    } else {
      try {
        const { data } = await client.graphql({
          query: createUploadData,
          variables: { input: formData },
        });
      } catch (error) {
        console.log("error", error);
      }
    }
    navigate(`/home/dataupload`);
  };

  const onError = (errors: any) => {
    console.error("Validation Errors: ", errors); // Logs any validation errors on submit
  };

  const handleProcessObjective = async (e) => {
    
    setLoadingLLMflag(true);
    e.preventDefault();
  

      try {
        const restOperation = post({
          apiName: "llmapi",
          path: "/api/processedmodel",
          options: {
            body: {objective },
          },
        });
        const operationResponse = await restOperation.response;
        const { data } = await operationResponse.body.json();
        setLoadingLLMflag(false);
        Object.keys(data).forEach((key) => {
          const theValue = data[key];
          if (key === "description") {
            setLLMJustification(theValue);
          } else {
            setValue(key, theValue);
          }
        });
      } catch (e) {
        console.log("e", e);
      }
  };

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Weather"
        title={editFlag ? initialUploadForm?.name || "Edit Custom Model" : "New Custom Model"}
        description="Configure your AI weather forecasting model, then upload the data to train it on."
        action={
          <PageBackButton label="Back to Models" onClick={() => navigate("/home/dataupload")} />
        }
      />

      {/* The form carries the page, so it takes the wide column; the data preview rides beside it on lg and stacks under it below that. */}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,400px)]">
        <div className="eai-panel">
          <form className="flex flex-col" onSubmit={handleSubmit(onSubmit, onError)}>
            <FieldLabel label="Name" error={errors.name?.message} />
            <input name="name" {...register("name")} className="eai-input mb-5" />

            <FieldLabel label='Describe Weather Forecasting Problem e.g. "I want to understand how wind will affect energy prices in New York?"' />
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={3}
              className="eai-input mb-2 resize-y"
            />
            <div className="mb-5 flex items-center gap-3">
              <button
                type="button"
                disabled={objective.trim().length === 0}
                className="eai-btn-cta"
                onClick={handleProcessObjective}
              >
                Configure Model
              </button>
              {/* Inline progress for the LLM round-trip that fills the fields below */}
              {loadingLLMflag && (
                <span className="eai-description-sm">Configuring&hellip;</span>
              )}
            </div>

            <FieldLabel label="Model Description" error={errors.description?.message} />
            <textarea
              name="description"
              {...register("description")}
              rows={3}
              className="eai-input mb-5 resize-y"
            />

            <FieldLabel label="Parameter" />
            <Select name="parameter" {...register("parameter")} containerClassName="mb-5 w-full">
              <option value="">Select Parameter of Interest</option>
              {PARAMETER_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>

            {/* Two per row on a tablet and up; one per row on a phone */}
            <div className="grid gap-x-4 sm:grid-cols-2">
              {SELECT_FIELDS.map(({ name, label, placeholder, options }) => (
                <div key={name}>
                  <FieldLabel label={label} error={errors[name]?.message} />
                  <Select name={name} {...register(name)} containerClassName="mb-5 w-full">
                    <option value="">{placeholder}</option>
                    {options.map(({ value, label: optionLabel }) => (
                      <option key={value} value={value}>
                        {optionLabel}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            </div>

            <FieldLabel label="File Upload" />
            {editFlag && initialUploadForm?.fileLocation && uploadNewFileFlag ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setUploadeNewFileFlag(false)}
                  className="eai-btn-outline-xs"
                >
                  Change
                </button>
                <span className="eai-description-sm truncate">
                  {initialUploadForm?.fileLocation.split("/")[1]}
                </span>
              </div>
            ) : (
              <input
                className="eai-input cursor-pointer file:mr-3 file:rounded-full file:border-0 file:bg-eai-surface-active file:px-3 file:py-1 file:font-outfit file:text-eai-body-xs file:text-eai-ink-muted"
                type="file"
                onChange={handleFileChange}
              />
            )}

            <div className="mt-8 flex justify-end">
              <button type="submit" className="eai-btn-cta">
                {editFlag ? "Update Model" : "Create Model"}
              </button>
            </div>
          </form>
        </div>

        <aside className="eai-panel">
          <h2 className="eai-panel-title">Data Preview</h2>
          {llmJustification && (
            <p className="eai-description-sm mt-3 rounded-md bg-eai-surface-sunken px-3 py-2">
              {llmJustification}
            </p>
          )}

          {csvData.length > 0 ? (
            // A wide CSV scrolls in its own box rather than stretching the panel
            <div className="mt-4 overflow-x-auto rounded-eai-card border border-eai-border">
              <table className="w-full border-separate border-spacing-0 text-left">
                <thead>
                  <tr>
                    {Object.keys(csvData[0]).map((header) => (
                      <th key={header} className={PREVIEW_HEAD_CLASS}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="odd:bg-eai-surface-sunken">
                      {Object.values(row).map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="whitespace-nowrap border-b border-eai-border px-3 py-1.5 font-inter text-eai-body-xs text-eai-ink"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="eai-description-sm mt-3">
              Upload a file to preview its contents here.
            </p>
          )}
        </aside>
      </div>
    </PageContainer>
  );
};

export default CustomModelForm;
